import React, { useRef, useState } from "react";
import clsx from "clsx";
import { isNil } from "lodash";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import ScaleLoader from "react-spinners/ScaleLoader";

interface SocialPropertySwiperProps {
    property: any;
    disableFancybox?: boolean;
}

/**
 * SocialPropertySwiper component displays Images with swiper on Listing Feed Page
 * @param {Object} props - Component props
 * @param {Object} props.property - Properties Details
 * @param {boolean} props.disableFancybox - If true, disables Fancybox lightbox (useful for feed cards where clicks should navigate)
 * @returns {JSX.Element} - Property Swiper for SinglePropertyFeed.
 */
const SocialPropertySwiper = ({ property, disableFancybox = false }: SocialPropertySwiperProps) => {
    const images = (property?.imageUrls || []) as string[];
    const statusIndicator = property?.statusIndicator as string | null;
    const status = statusIndicator ? statusIndicator.toLowerCase() : null;
    const swiperRef = useRef<any>(null);
    const [loadedImages, setLoadedImages] = useState(Array(images.length).fill(false) as boolean[]);

    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => {
            const newLoaded = [...prev];
            newLoaded[index] = true;
            return newLoaded;
        });
    };

    const handleSlideChange = (swiper: any) => {
        localStorage.setItem(`swiper-index-${property.id}`, String(swiper.activeIndex));
    };

    const handleArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <Swiper
            ref={swiperRef}
            modules={[EffectFade, Navigation]}
            loop={false}
            navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            }}
            data-cy="property-image-swiper"
            speed={400}
            effect={"fade"}
            className="property-image-swiper !tw-object-cover [&_.swiper-wrapper]:!tw-max-w-none [&_.image-container]:!tw-max-w-none"
            onSlideChange={handleSlideChange}
        >
            {images.map((image, index) => (
                <SwiperSlide className="swiper-slide" key={index}>
                    <div
                        className="image-container"
                        {...(!disableFancybox && {
                            "data-fancybox": `gallery-${property.id}`,
                            "data-src": image,
                        })}
                    >
                        {statusIndicator && (
                            <span
                                className={clsx(
                                    "tw-absolute tw-top-[5%] tw-py-1 tw-px-4 tw-rounded-[500px] tw-z-[40] tw-right-[5%]",
                                    {
                                        "tw-bg-[#AAD880]": status === "active",
                                        "tw-bg-[#e7ac2d]": status === "contract",
                                        "tw-bg-[#494949] tw-text-white": status === "closed",
                                    },
                                )}
                            >
                                {statusIndicator}
                            </span>
                        )}
                        {(!loadedImages[index] || isNil(loadedImages[index])) && (
                            <div className="tw-flex !tw-justify-center !tw-items-center tw-h-full">
                                <ScaleLoader color="#63c19f" />
                            </div>
                        )}
                        <img
                            src={image}
                            alt={`Slide ${index}`}
                            onLoad={() => handleImageLoad(index)}
                            style={{ display: loadedImages[index] ? "block" : "none" }}
                            className="!tw-w-full !tw-h-full tw-object-cover tw-object-center tw-rounded-xl"
                        />
                    </div>
                </SwiperSlide>
            ))}
            <div className="swiper-button-prev !tw-left-5" onClick={handleArrowClick} />
            <div className="swiper-button-next !tw-right-5" onClick={handleArrowClick} />
        </Swiper>
    );
};

export default SocialPropertySwiper;
